import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useSelector } from 'react-redux';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Spinner from 'src/components/Spinner';
import { fetchAssignmentsByUser } from 'src/services/TaskService';

const Home = () => {
  const user = useSelector((state) => state.user.currentUser);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const normalizeAssignments = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.assignments)) return response.assignments;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.result)) return response.result;
    return [];
  };

  const getParticipantName = (assignment) =>
    assignment.participant_name ||
    `${assignment.participant_first_name || ''} ${
      assignment.participant_last_name || ''
    }`.trim();

  const getAssessorName = (assignment) =>
    assignment.assessor_name ||
    `${assignment.assessor_first_name || ''} ${
      assignment.assessor_last_name || ''
    }`.trim();

  const clientId = useMemo(
    () => Number(user?.clientId ?? user?.client_id ?? 0),
    [user]
  );
  const isDesignationAdmin =
    String(user?.designation || '').trim().toLowerCase() === 'admin';
  const canDownloadAsExcel = isDesignationAdmin && clientId !== 3;
  const groupedAssignments = useMemo(() => {
    const groups = {};
    rows.forEach((assignment) => {
      const participantName = getParticipantName(assignment) || '-';
      if (!groups[participantName]) groups[participantName] = [];
      groups[participantName].push(assignment);
    });
    return Object.keys(groups)
      .sort((a, b) => a.localeCompare(b))
      .map((participantName) => ({
        participantName,
        assignments: groups[participantName]
      }));
  }, [rows]);

  const getParticipantStatus = (assignments) => {
    if (!assignments || assignments.length === 0) return 'Pending';
    const allCompleted = assignments.every(
      (assignment) =>
        String(assignment.status || '').trim().toLowerCase() === 'completed'
    );
    return allCompleted ? 'Completed' : 'Pending';
  };

  const loadAssignments = useCallback(async () => {
    if (!user?.email || !clientId || clientId === 3) return;
    setLoading(true);
    try {
      const response = await fetchAssignmentsByUser({
        email: user.email,
        clientId
      });
      setRows(normalizeAssignments(response));
    } catch (error) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, user]);

  const downloadAsExcel = () => {
    const headers = ['Participant name', 'Assessor name', 'Relation', 'Status'];
    const csvRows = rows.map((assignment) => [
      getParticipantName(assignment),
      getAssessorName(assignment),
      assignment.relation || '-',
      assignment.status || '-'
    ]);
    const escapeCell = (val) => `"${String(val).replace(/"/g, '""')}"`;
    const csv = [headers, ...csvRows]
      .map((line) => line.map(escapeCell).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'assignments.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadAssignments();
  }, [loadAssignments]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      loadAssignments();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadAssignments();
      }
    };
    window.addEventListener('focus', handleFocusRefresh);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('focus', handleFocusRefresh);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadAssignments]);

  return (
    <>
      <Helmet>
        <title>Home</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth={false}>
          <Spinner showLoading={loading} />
          <Card>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  rowGap: 1
                }}
              >
                <Typography color="textPrimary" variant="h3">
                  Home
                </Typography>
                {canDownloadAsExcel ? (
                  <Button
                    color="primary"
                    disabled={rows.length === 0}
                    onClick={downloadAsExcel}
                    variant="contained"
                  >
                    Download as Excel
                  </Button>
                ) : null}
              </Box>
              <Box sx={{ mt: 3 }}>
                <PerfectScrollbar>
                  <Box sx={{ minWidth: 900 }}>
                    {canDownloadAsExcel ? (
                      <>
                        {groupedAssignments.length > 0 ? (
                          <Box
                            sx={{
                              display: 'flex',
                              px: 3,
                              pb: 1
                            }}
                          >
                            <Box sx={{ width: '33.33%' }}>
                              <Typography color="textSecondary" variant="subtitle2">
                                Participant Name
                              </Typography>
                            </Box>
                            <Box sx={{ width: '33.33%', textAlign: 'center' }}>
                              <Typography color="textSecondary" variant="subtitle2">
                                Status
                              </Typography>
                            </Box>
                            <Box sx={{ width: '33.33%', textAlign: 'right' }}>
                              <Typography color="textSecondary" variant="subtitle2">
                                Show Details
                              </Typography>
                            </Box>
                          </Box>
                        ) : null}
                        {groupedAssignments.map((group) => (
                          <Accordion key={group.participantName}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                              <Box
                                sx={{
                                  width: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  pr: 2
                                }}
                              >
                                <Box sx={{ width: '33.33%' }}>
                                  <Typography variant="h5">
                                    {group.participantName} ({group.assignments.length})
                                  </Typography>
                                </Box>
                                <Box sx={{ width: '33.33%', textAlign: 'center' }}>
                                  <Typography
                                    style={{
                                      color:
                                        getParticipantStatus(group.assignments) ===
                                        'Completed'
                                          ? 'green'
                                          : 'red'
                                    }}
                                    variant="subtitle2"
                                  >
                                    {getParticipantStatus(group.assignments)}
                                  </Typography>
                                </Box>
                                <Box sx={{ width: '33.33%' }} />
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Table>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Assessor name</TableCell>
                                    <TableCell>Relation</TableCell>
                                    <TableCell>Status</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {group.assignments.map((assignment) => (
                                    <TableRow
                                      key={assignment.id || assignment.assignment_uuid}
                                    >
                                      <TableCell>
                                        {getAssessorName(assignment)}
                                      </TableCell>
                                      <TableCell>{assignment.relation || '-'}</TableCell>
                                      <TableCell>
                                        <Typography
                                          style={{
                                            color:
                                              assignment.status === 'Pending'
                                                ? 'red'
                                                : assignment.status === 'Completed'
                                                ? 'green'
                                                : 'inherit'
                                          }}
                                          variant="body2"
                                        >
                                          {assignment.status || '-'}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </AccordionDetails>
                          </Accordion>
                        ))}
                      </>
                    ) : (
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Participant name</TableCell>
                            <TableCell>Assessor name</TableCell>
                            <TableCell>Relation</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((assignment) => (
                            <TableRow key={assignment.id || assignment.assignment_uuid}>
                              <TableCell>{getParticipantName(assignment)}</TableCell>
                              <TableCell>{getAssessorName(assignment)}</TableCell>
                              <TableCell>{assignment.relation || '-'}</TableCell>
                              <TableCell>
                                <Typography
                                  style={{
                                    color:
                                      assignment.status === 'Pending'
                                        ? 'red'
                                        : assignment.status === 'Completed'
                                        ? 'green'
                                        : 'inherit'
                                  }}
                                  variant="body2"
                                >
                                  {assignment.status || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {assignment.assignment_uuid ? (
                                  <a
                                    href={`${window.location.origin}/app/assessment/${assignment.assignment_uuid}`}
                                    rel="noreferrer"
                                    target="_blank"
                                    style={{ textDecoration: 'underline' }}
                                  >
                                    TAKE ASSESSMENT
                                  </a>
                                ) : (
                                  '-'
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                    {rows.length === 0 && !loading ? (
                      <Box sx={{ py: 2 }}>
                        <Typography align="center" variant="body1">
                          No assignments found
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                </PerfectScrollbar>
              </Box>
            </CardContent>
          </Card>
        </Container>
      </Box>
    </>
  );
};

export default Home;
